import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  CheckCircle, 
  Loader2, 
  BookOpen, 
  Play, 
  ArrowRight,
  Download,
  Mail,
  Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StoreSuccessPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState(null);
  const [isBundle, setIsBundle] = useState(false);
  const [topicCount, setTopicCount] = useState(0);
  const [error, setError] = useState(null);

  const purchaseId = searchParams.get('purchase_id');
  const bundleId = searchParams.get('bundle_id');
  const accessToken = searchParams.get('access_token');

  useEffect(() => {
    if ((purchaseId || bundleId) && accessToken) {
      verifyPurchase();
    } else {
      setError('Invalid purchase link');
      setLoading(false);
    }
  }, [purchaseId, bundleId, accessToken]);

  const verifyPurchase = async () => {
    try {
      let url = `${API_URL}/api/store/success?access_token=${accessToken}`;
      if (bundleId) {
        url += `&bundle_id=${bundleId}`;
      } else {
        url += `&purchase_id=${purchaseId}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (response.ok) {
        setPurchase(data.purchase);
        setIsBundle(data.is_bundle || false);
        setTopicCount(data.topic_count || 1);
      } else {
        setError(data.detail || 'Purchase verification failed');
      }
    } catch (err) {
      setError('Failed to verify purchase');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#C5A059] animate-spin mx-auto mb-4" />
          <p className="text-[#64748B]">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2">
            Something went wrong
          </h1>
          <p className="text-[#64748B] mb-6">{error}</p>
          <Link to="/store">
            <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
              Back to Store
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 max-w-lg w-full">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {isBundle ? (
              <Gift className="w-10 h-10 text-green-500" />
            ) : (
              <CheckCircle className="w-10 h-10 text-green-500" />
            )}
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A] mb-2">
            {isBundle ? 'Bundle Purchase Successful!' : 'Purchase Successful!'}
          </h1>
          <p className="text-[#64748B]">
            {isBundle 
              ? `You now have access to ${topicCount} topics!` 
              : 'Thank you for your purchase. You now have access to your content.'
            }
          </p>
        </div>

        {/* Purchase Details */}
        <div className="bg-[#F8FAFC] rounded-xl p-4 mb-6">
          {isBundle ? (
            <>
              <h3 className="font-semibold text-[#0F172A] mb-3">
                {topicCount}-Topic Bundle
              </h3>
              {purchase?.topic_names && (
                <ul className="text-sm text-[#64748B] mb-3 space-y-1">
                  {purchase.topic_names.map((name, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      {name}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between text-sm border-t border-[#E2E8F0] pt-3">
                <span className="text-[#64748B]">
                  {purchase?.tier === 'cards_videos' ? 'Cards + Videos Bundle' : 'Revision Cards'}
                </span>
                <span className="font-semibold text-[#0F172A]">
                  £{(purchase?.amount / 100).toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-semibold text-[#0F172A] mb-3">{purchase?.topic_name}</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[#64748B]">
                  {purchase?.tier === 'cards_videos' ? 'Cards + Videos Bundle' : 'Revision Cards'}
                </span>
                <span className="font-semibold text-[#0F172A]">
                  £{(purchase?.amount / 100).toFixed(2)}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Access Instructions */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3 p-4 bg-[#C5A059]/5 rounded-xl border border-[#C5A059]/20">
            <Mail className="w-5 h-5 text-[#C5A059] mt-0.5" />
            <div>
              <h4 className="font-semibold text-[#0F172A] text-sm">Confirmation Email</h4>
              <p className="text-[#64748B] text-sm">
                We've sent a receipt to <strong>{purchase?.email}</strong>
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
            <BookOpen className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-green-800 text-sm">Access Your Content</h4>
              <p className="text-green-700 text-sm">
                {isAuthenticated 
                  ? 'View your purchased content in your dashboard anytime.'
                  : 'Bookmark this link to access your content anytime.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <Link to="/dashboard" className="block">
              <Button className="w-full h-12 bg-[#C5A059] hover:bg-[#B8934E] text-white">
                <BookOpen className="w-5 h-5 mr-2" />
                View in Dashboard
              </Button>
            </Link>
          ) : (
            <Link 
              to={`/store/view?token=${accessToken}`}
              className="block"
            >
              <Button className="w-full h-12 bg-[#C5A059] hover:bg-[#B8934E] text-white">
                <BookOpen className="w-5 h-5 mr-2" />
                View Content Now
              </Button>
            </Link>
          )}
          
          <Link to="/store" className="block">
            <Button variant="outline" className="w-full h-12 border-[#E2E8F0]">
              Continue Shopping
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Guest Access Link */}
        {!isAuthenticated && (
          <div className="mt-6 p-4 bg-[#F8FAFC] rounded-xl text-center">
            <p className="text-sm text-[#64748B] mb-2">
              Save your access link:
            </p>
            <code className="block text-xs bg-white p-2 rounded border border-[#E2E8F0] break-all text-[#0F172A]">
              {window.location.origin}/store/view?token={accessToken}
            </code>
            <p className="text-xs text-[#94A3B8] mt-2">
              Or <Link to="/login" className="text-[#C5A059] hover:underline">create an account</Link> for easier access
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
